import { action, computed, observable, toJS } from "mobx";
import { compact, flatten, isEmpty, uniq } from "lodash";
import { ObservableGraphqlQuery } from "@clientUtils";
import {
  CustomerEventLog_GetEvents,
  CustomerProfile_DeleteCustomer,
  CustomerProfile_GetProfile,
  CustomerProfile_MoveCustomer,
  CustomerProfile_ToggleCustomerNoApproval,
  CustomerProfile_ToggleCustomerSettings,
  CustomerSettingsKey,
  CustomerType,
  NodeValidationErrorType,
  SearchProfile_UpdateCustomerRef
} from "@graphql";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import {
  CustomerEventLogStore,
  CustomerSelectorDropinRef,
  OkCancelDialogOptions
} from "@containers";

import { FauxItem } from "../CustomerContactPersons/CustomerContactPersons";

import {
  ContactPersonLocation,
  CUSTOMER_SLIDER,
  CustomerEditorLocation
} from "@navigation";
import { CustomerDocumentTypes } from "@bonliva-crm/shared";

export class CustomerProfileStore {
  @observable selectedContactPersonIds: string[] = [];
  @observable selectedAreasOfExpertise: string[] = [];
  @observable selectedSearchProfiles: string[] = null;
  @observable showAllContactPersons = false;
  @observable showDeletedContactPersons = false;
  @observable isLoadingCustomerSettings = false;
  @observable tab = "events";
  @observable isMoving = false;
  @observable files: File[] = [];
  @observable filesNoApproval: File = null;
  @observable openUploader = false;
  @observable contactPersonToDelete: { id: string; name: string } | null = null;

  noApprovalValue: boolean = false;

  dropzone: { open: () => void };
  defaultDocumentType = "";

  customerSelectorRef = new CustomerSelectorDropinRef();
  customerEventLogStore: CustomerEventLogStore;

  private _profile = new ObservableGraphqlQuery(
    () =>
      CustomerProfile_GetProfile.funcWatch(
        {
          id: this.id,
          customerGroup: this.customerGroup,
          showDeleted: this.showDeletedContactPersons
        },
        { fetchPolicy: "network-only" }
      ),
    {
      onError: (err) => store().snackbars.error(`Cannot fetch data\nReason: ${err}`)
    }
  );

  constructor(public readonly id: string, public readonly customerGroup: string) {
    (window as any).customerProfileStore = this;
    this._profile.onFetch.on(this.refetchEvents);
  }

  @computed get profile() {
    return this._profile.data ? this._profile.data.customerById : null;
  }

  @computed get hasStandingCertificateForTR(): boolean {
    return this.profile
      ? this.profile.documents.some(
          (x) => x.DocumentType === CustomerDocumentTypes.StandingCertificateForTR
        )
      : false;
  }

  @computed get searchProfiles() {
    return this._profile.data ? this._profile.data.searchProfiles : null;
  }

  @computed get isLoading() {
    return this._profile.loading;
  }

  @computed get error() {
    return this._profile.error;
  }

  @computed get canPromoteToOrg() {
    return this.profile && this.profile.Type !== CustomerType.Region;
  }

  @computed get rootCustomerId() {
    if (!this.profile || !this.profile.Parents) {
      return null;
    }

    if (this.profile.Type === CustomerType.Region) {
      return this.profile._id;
    }

    const parent = this.profile.Parents.find((p) => p.Type === CustomerType.Region);

    return parent ? parent.Id : null;
  }

  @computed get selectedContactPersons() {
    if (!this.profile) {
      return [];
    }

    return this.profile.ContactPersons.filter((p) =>
      this.selectedContactPersonIds.includes(p._id)
    );
  }

  @computed get contactPersonsAoe() {
    return uniq(
      this.profile?.ContactPersons.filter((x) => x.AreasOfExpertise.length > 0).flatMap(
        (x) => x.AreasOfExpertise
      )
    );
  }

  @computed get isDisabledNeedSignatureTROkInApp() {
    return (
      this.profile?.timeReportTemplateSettings.length === 0 ||
      this.profile?.noApproval ||
      this.profile?.needSignature ||
      this.profile?.noApprovalMonthly ||
      this.profile?.noApprovalShortening ||
      this.profile?.noApprovalSpecial
    );
  }

  @computed get eventLogVariables(): CustomerEventLog_GetEvents.Variables {
    // show all event in the root (don't filter on contact persons)
    // but in subgroup show events filtered on available (or selected) cps
    if (!this.profile) {
      return null;
    }

    const contactPersonIds = isEmpty(this.selectedContactPersonIds)
      ? null
      : this.selectedContactPersonIds;

    const customerGroup = "";

    return {
      filter: {
        ContactPersonId: contactPersonIds,
        CustomerGroup: customerGroup,
        // NodeId: "",
        AreasOfExpertise: this.selectedAreasOfExpertise.slice(),
        Role: flatten(this.selectedContactPersons.map((cp) => cp.Roles)),
        CustomerId: this.id
      }
    };
  }

  /**
   * Contact Persons available to the contact persons list after adjusting by
   * selected areas of expertise and children
   */
  @computed get availableContactPersons() {
    if (!this.profile || !this.profile.ContactPersons) {
      return [];
    }

    const deletedFilter = (item: { Deleted: boolean }) =>
      this.showDeletedContactPersons ? true : !item.Deleted;

    const aoeFilter = (item: { AreasOfExpertise: string[] }) =>
      isEmpty(this.selectedAreasOfExpertise)
        ? true
        : item.AreasOfExpertise.hasAny(this.selectedAreasOfExpertise);

    if (this.profile.Type === CustomerType.Region && !this.customerGroup) {
      return this.profile.ContactPersons.filter(deletedFilter)
        .filter((x) => (this.showAllContactPersons ? true : isEmpty(x.CustomerGroups)))
        .filter(aoeFilter);
    }

    if (this.profile.Type === CustomerType.Region && this.customerGroup) {
      return this.profile.ContactPersons.filter(deletedFilter)
        .filter((x) =>
          this.showAllContactPersons
            ? true
            : x.CustomerGroups.includes(this.customerGroup)
        )
        .filter(aoeFilter);
    }

    if (
      this.profile.Type === CustomerType.Hospital ||
      this.profile.Type === CustomerType.Department
    ) {
      return this.profile.ContactPersons.filter(deletedFilter).filter(aoeFilter);
    }
  }

  /**
   * When some roles are missing, add these fake placeholder-type contact persons
   * to the list
   */
  @computed get fauxContactPersons(): FauxItem[] {
    const result: FauxItem[] = [];

    const roleError = this.profile.ValidationErrors.find(
      (e) => e.Type === NodeValidationErrorType.CustomerInsufficientRoles
    );

    const profError = this.profile.ValidationErrors.find(
      (e) => e.Type === NodeValidationErrorType.CustomerInsufficientBookingProfessions
    );

    if (roleError) {
      result.push(
        ...roleError.Params.roles.map((role) => ({
          title: `${locale.AddContactPersonText} ${role}`,
          desc: locale.AddContactPersonSubText,
          onClick: () => this.openContactPersonEditor(null, { roles: [role] })
        }))
      );
    }

    if (profError && !isEmpty(profError.Params.professions)) {
      const profs = profError.Params.professions.join(", ");

      result.push({
        title: `${locale.AddContactPersonText} ${profs}`,
        desc: `${locale.AddContactPersonSubText} ${profs}`,
        onClick: () =>
          this.openContactPersonEditor(null, {
            professions: toJS(profError.Params.professions)
          })
      });
    }

    return result;
  }

  @computed get customerCantBeMovedReasons() {
    return compact([
      !store().user.hasPermission("CanUpdateConsultantGDPR") && locale.NoAccess
    ]);
  }

  deleteCustomer = () => {
    const { _id, Name } = this.profile;
    const message = locale.DeleteCustomerPersonConfirm(Name);

    store().ui.confirmDelete(message, (confirmed) => {
      if (!confirmed) {
        return;
      }
      CustomerProfile_DeleteCustomer.func({ id: _id })
        .then(() => {
          store().snackbars.success(locale.CustomerDeleted);
          store().routing.history.back();
        })
        .catch((e) => store().snackbars.error(`Cannot delete form.\n Reason: ${e}`));
    });
  };

  promoteToOrg = () => {
    const options = new OkCancelDialogOptions("Promote customer to organisation");

    store()
      .ui.confirmDialog.confirm(options)
      .then((value) => {
        if (value === "cancel") {
          return;
        }

        return this._moveCustomer();
      })
      .catch((err) => {
        store().snackbars.error(`Cannot promote customer\nReason: ${err}`);
      });
  };

  moveCustomer = () => {
    if (!this.customerSelectorRef.current) {
      return;
    }

    let parentId: string;

    this.customerSelectorRef.current
      .select({
        selectorProps: {
          initialFilter: { customerType: CustomerType.Region },
          disabledCustomerTypeOptions: [CustomerType.Department]
        }
      })
      .then((selected) => {
        if (!selected) {
          return;
        }

        parentId = selected._id;
        const msg = `Are you sure you want to move this customer under ${selected.Name}`;
        return store().ui.confirmDialog.confirm(new OkCancelDialogOptions(msg));
      })
      .then((value) => {
        if (value !== "ok") {
          return;
        }

        return this._moveCustomer(parentId);
      })
      .catch((err) => {
        store().snackbars.error(`Cannot move customer\nReason: ${err}`);
      });
  };

  refetchEvents = () => {
    if (this.isLoading) {
      return;
    }

    if (this.customerEventLogStore) {
      return this.customerEventLogStore.refetch(this.eventLogVariables.filter);
    }

    this.customerEventLogStore = new CustomerEventLogStore(
      this.id,
      this.customerGroup || null,
      this.eventLogVariables
    );
  };

  @action handleCloseUploader = () => {
    this.openUploader = false;
    this.filesNoApproval = null;
  };

  @action requestUpload = () =>
    store()
      .upload.requestFileUpload({
        multiple: false
      })
      .then((files) => {
        this.filesNoApproval = files[0];
      });

  @action closeDeleteContactDialog = (refetch: boolean) => {
    this.contactPersonToDelete = null;
    refetch && this._profile.refetch();
  };

  @action openDeleteContactDialog = (id: string, name: string) =>
    (this.contactPersonToDelete = {
      id,
      name
    });

  @action.bound handleContactPersonSelect(id: string) {
    this.selectedContactPersonIds.toggle(id);
    this.refetchEvents();
  }

  @action.bound handleContactPersonSelectAll() {
    this.selectedContactPersonIds =
      this.selectedContactPersonIds.length > 0
        ? []
        : (this.selectedContactPersonIds = this.profile.ContactPersons.map((c) => c._id));
    this.refetchEvents();
  }

  @action.bound handleAreaOfExpertiseSelect(v: string) {
    if (!this.profile.AreasOfExpertise.includes(v)) {
      return;
    }

    this.selectedAreasOfExpertise.toggle(v);
  }

  @action.bound openTab(value: "events" | "documents" | "gdpr") {
    this.tab = value;
  }

  @action.bound openUpload(defaultDocumentType?: string) {
    if (!this.dropzone) {
      return;
    }

    this.defaultDocumentType = defaultDocumentType;
    this.dropzone.open();
  }

  openContactPersonEditor = (
    id: string | null,
    initialState: { roles?: string[]; professions?: string[]; group?: string } = {}
  ) => {
    const initialData = {
      BooksProfessions: initialState.professions,
      Roles: initialState.roles,
      CustomerGroups: compact([this.customerGroup, initialState.group])
    };

    return store().navigation.open(
      CUSTOMER_SLIDER,
      new ContactPersonLocation(this.id, id, initialData)
    );
  };

  addCustomer = (group: string) => {
    const initialValues = {
      OrganizationType: this.profile.OrganizationType,
      RecruitingPortalType: this.profile.RecruitingPortalType,
      forceCallNumber: null,
      CustomerGroups: compact([group]),
      Type:
        this.profile.Type === CustomerType.Region
          ? CustomerType.Hospital
          : CustomerType.Department,
      Address: {
        Country: this.profile.Address ? this.profile.Address.Country : "",
        Region: this.profile.Address ? this.profile.Address.Region : "",
        City: this.profile.Address ? this.profile.Address.City : ""
      },
      Region:
        this.profile.Type === CustomerType.Region
          ? { label: this.profile.Name, value: this.profile._id }
          : {
              label: this.profile.Parents[0].Name,
              value: this.profile.Parents[0].Id
            },
      Hospital:
        this.profile.Type === CustomerType.Hospital
          ? { label: this.profile.Name, value: this.profile._id }
          : undefined,
      Professions: this.profile.Professions,
      AOESettings: []
    };

    store()
      .navigation.getInstance(CUSTOMER_SLIDER)
      .navigate(new CustomerEditorLocation(null, initialValues));
  };

  @action toggleShowAllContactPersons = (v: boolean) => {
    this.showAllContactPersons = v;

    if (!this.customerGroup) {
      this.refetchEvents();
      return;
    }

    this._profile.refetch({
      id: this.id,
      customerGroup: this.showAllContactPersons ? "" : this.customerGroup,
      showDeleted: this.showAllContactPersons
    });
  };

  @action toggleShowDeletedContactPersons = (v: boolean) => {
    this.showDeletedContactPersons = v;
    this._profile.refetch({
      id: this.id,
      customerGroup: this.showAllContactPersons ? "" : this.customerGroup,
      showDeleted: this.showDeletedContactPersons
    });
  };

  @action private _moveCustomer = (parentId?: string) => {
    this.isMoving = true;

    return CustomerProfile_MoveCustomer.func({ id: this.profile._id, parentId })
      .then((res) => {
        if (res.data.result.customer) {
          store().snackbars.success(locale.CustomerMoved);
        } else {
          const e = res.data.result.validationErrors.join("\n\n");
          store().snackbars.error(e);
        }

        return this._profile.refetch();
      })
      .finally(() => {
        this.isMoving = false;
      });
  };

  @action handleChangeSearchProfiles = (values) => {
    this.selectedSearchProfiles = values.map((x) => x._id);
  };

  @action submitChangeSearchProfiles = () => {
    SearchProfile_UpdateCustomerRef.func(
      {
        id: this.id,
        profileIds: this.selectedSearchProfiles
      },
      { refetchQueries: [CustomerProfile_GetProfile.OperationName] }
    )
      .then(() => {
        store().snackbars.success(locale.Saved);
      })
      .catch((e) => {
        store().snackbars.error(`Error: ${e}`);
      });
    this.selectedSearchProfiles = null;
  };

  @action toggleCustomerSettings = (value: boolean, key: string) => {
    this.isLoadingCustomerSettings = true;

    CustomerProfile_ToggleCustomerSettings.func(
      {
        id: this.id,
        value,
        key: key as CustomerSettingsKey
      },
      { refetchQueries: [CustomerProfile_GetProfile.OperationName] }
    )
      .then(() => {
        store().snackbars.success(locale.Saved);
      })
      .catch((e) => {
        store().snackbars.error(`Error: ${e}`);
      })
      .finally(() => {
        this.isLoadingCustomerSettings = false;
      });
  };

  @action handleNoApprovalDocument = (value: boolean) => {
    this.noApprovalValue = value;

    if (value && !this.hasStandingCertificateForTR) {
      this.openUploader = true;
    } else {
      this.submitNoApprovalDocument();
    }
  };

  @action submitNoApprovalDocument = () => {
    this.isLoadingCustomerSettings = true;

    CustomerProfile_ToggleCustomerNoApproval.func(
      {
        id: this.id,
        value: this.noApprovalValue,
        file: this.filesNoApproval,
        documentType: CustomerDocumentTypes.StandingCertificateForTR
      },
      { refetchQueries: [CustomerProfile_GetProfile.OperationName] }
    )
      .then(() => {
        store().snackbars.success(locale.Saved);
      })
      .catch((e) => {
        store().snackbars.error(`Error: ${e}`);
      })
      .finally(() => {
        this.isLoadingCustomerSettings = false;
        this.handleCloseUploader();
      });
  };
}
